import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import {
  addExpediteDailyPart,
  deleteExpediteDailyPart,
  getExpediteDailyParts,
  resetExpediteDailyParts,
  saveExpediteDailyParts,
  updateExpediteDailyPart,
} from '~/store/expedite/actions';
import { convertTimeStampToMoment } from '~/utils/moment';
import { usePagination } from '~/pages/Expedite/hooks';
import PF2MDataTable from '~/components/PF2MDataTable';
import styles from './styles';

const normalizeTimestamp = (timestamp, toMilliseconds = true) => {
  if (!timestamp) return timestamp;
  const timestampString = String(timestamp);

  const isInSeconds = timestampString.length <= 10;

  if (toMilliseconds) {
    return isInSeconds ? timestamp * 1000 : timestamp;
  }
  return isInSeconds ? timestamp : timestamp / 1000;
};

const ExpediteReport = ({ classes }) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const expediteDailyParts = useSelector(state => state.expedite.expediteDailyParts);
  const expediteMaterials = useSelector(state => state.expedite.expediteMaterials);
  const filters = useSelector(state => state.expedite.expediteDailyPartsFilters);
  const exceptionTypes = useSelector(state => state.manager.exceptionTypes);
  const subElements = useSelector(state => state.manager.subElements);
  const materials = useSelector(state => state.manager.materials);
  const {
    expediteDailyPartsPagination,
  } = useSelector(state => state.expedite.pagination);

  const expediteDailyPartsList = useMemo(() => expediteDailyParts.map(part => ({
    ...part,
    start_timestamp: normalizeTimestamp(part.start_timestamp, true),
    end_timestamp: normalizeTimestamp(part.end_timestamp, true),
  })), [expediteDailyParts]);

  const externalProductList = useMemo(() => {
    const materialMap = new Map(
      materials.map(material => [material.id, material.name]),
    );

    return expediteMaterials.map(exMaterial => ({
      ...exMaterial,
      name: materialMap.get(exMaterial.material_id) ?? null,
    }));
  }, [materials, expediteMaterials]);

  const validateIsNewInsertRow = rowData => rowData.id <= 0 && rowData.update_status === 'I';

  const validateEmptyFields = (rowData, key) => {
    const value = String(rowData[key])?.trim() || '';
    if (!value || value === '0' || value === '') {
      return { isValid: false, helperText: translate('validation:RequiredField') };
    }
    return { isValid: true };
  };

  const validateDateTimeFields = (rowData, key) => {
    const startTimestamp = rowData.start_timestamp
      ? convertTimeStampToMoment(rowData.start_timestamp / 1000)
      : null;
    const endTimestamp = rowData.end_timestamp
      ? convertTimeStampToMoment(rowData.end_timestamp / 1000)
      : null;
    if (key === 'start_timestamp' && (!startTimestamp || !startTimestamp.isValid())) {
      return { isValid: false, helperText: translate('validation:InvalidDate') };
    }
    if (key === 'end_timestamp' && (!endTimestamp || !endTimestamp.isValid())) {
      return { isValid: false, helperText: translate('validation:InvalidDate') };
    }
    if (startTimestamp?.isValid() && endTimestamp?.isValid()) {
      if (key === 'start_timestamp' && startTimestamp.isAfter(endTimestamp)) {
        return {
          isValid: false,
          helperText: translate('validation:FinalDateSmallerThanInitialDate'),
        };
      }
      if (key === 'end_timestamp' && endTimestamp.isBefore(startTimestamp)) {
        return {
          isValid: false,
          helperText: translate('validation:FinalDateSmallerThanInitialDate'),
        };
      }
    }
    return { isValid: true };
  };

  const validateWeight = (rowData) => {
    const emptyFieldsValidation = validateEmptyFields(rowData, 'weight');
    if (!emptyFieldsValidation.isValid) return emptyFieldsValidation;

    const value = rowData?.weight?.toString();
    const decimalRegex = /^\d+(\.\d{1,5})?$/;

    if (!decimalRegex.test(value)) {
      return { isValid: false, helperText: translate('validation:JustNumbers') };
    }
    const [intPart, decimalPart = ''] = value?.split('.') || [];
    if (intPart?.length > 9) {
      return {
        isValid: false,
        helperText: translate('validation:LimitCharactersExceeded'),
      };
    }
    if (decimalPart?.length > 5) {
      return {
        isValid: false,
        helperText: translate('validation:LimitCharactersExceeded'),
      };
    }
    return { isValid: true };
  };

  const validateTicketOrder = (rowData, allRows) => {
    const emptyFieldsValidation = validateEmptyFields(rowData, 'ticket_order');
    if (!emptyFieldsValidation.isValid) return emptyFieldsValidation;

    const onlyNumbersRegex = /^[0-9]+$/;
    const isNotANumber = !onlyNumbersRegex.test(rowData?.ticket_order);

    if (isNotANumber) {
      return { isValid: false, helperText: translate('validation:JustNumbers') };
    }

    const value = String(rowData?.ticket_order)?.trim() || '';

    const hasDuplicateTicketOrder = allRows.filter(
      row => String(row.ticket_order)?.trim() === value
          && row.id !== rowData.id,
    );
    if (hasDuplicateTicketOrder.length > 0) {
      return { isValid: false, helperText: translate('validation:DuplicatedRegister') };
    }

    const isTooLong = value.length > 9;
    if (isTooLong) {
      return { isValid: false, helperText: translate('validation:LimitCharactersExceeded') };
    }

    return { isValid: true };
  };

  const columns = [
    {
      title: translate('common:EntryDateAndTime'),
      field: 'start_timestamp',
      customType: 'dateTimeByTimestamp',
      required: true,
      editable: 'always',
      validate: rowData => validateDateTimeFields(rowData, 'start_timestamp'),
    },
    {
      title: translate('common:ExitDateAndTime'),
      field: 'end_timestamp',
      customType: 'dateTimeByTimestamp',
      required: true,
      editable: 'always',
      validate: rowData => validateDateTimeFields(rowData, 'end_timestamp'),
    },
    {
      title: translate('common:TicketNumber'),
      field: 'ticket_order',
      editable: (_columnDef, rowData) => validateIsNewInsertRow(rowData),
      required: true,
      validate: rowData => validateTicketOrder(rowData, expediteDailyPartsList),
    },
    {
      title: translate('common:Material'),
      field: 'external_product_id',
      customType: 'filterLookup',
      lookup: externalProductList,
      lookupKey: 'external_product_id',
      required: true,
      editable: 'always',
      validate: rowData => validateEmptyFields(rowData, 'external_product_id'),
    },
    {
      title: translate('common:NetWeight'),
      field: 'weight',
      required: true,
      editable: 'always',
      validate: rowData => validateWeight(rowData),
    },
    {
      title: translate('common:OriginLot'),
      field: 'element_point_id',
      customType: 'filterLookup',
      lookup: subElements,
      required: true,
      editable: 'always',
      validate: rowData => validateEmptyFields(rowData, 'element_point_id'),
    },
    {
      title: translate('common:EntryType'),
      field: 'exception_type',
      lookup: exceptionTypes,
      editable: 'never',
    },
  ];

  const handleOnAdd = (newData) => {
    const newDailyPart = {
      ...newData,
      start_timestamp: moment().unix(),
      end_timestamp: moment().unix(),
    };
    dispatch(addExpediteDailyPart(newDailyPart));
  };

  const handleOnChange = (updatedDailyPartList) => {
    updatedDailyPartList.forEach((dailyPart) => {
      const newDailyPart = {
        ...dailyPart,
        ticket_order: dailyPart?.ticket_order ? parseInt(dailyPart.ticket_order, 10) : null,
        weight: dailyPart?.weight ? parseFloat(dailyPart.weight) : null,
        start_timestamp: normalizeTimestamp(dailyPart.start_timestamp, false),
        end_timestamp: normalizeTimestamp(dailyPart.end_timestamp, false),
      };
      dispatch(updateExpediteDailyPart(newDailyPart));
    });
  };

  const handleDiscard = () => {
    dispatch(resetExpediteDailyParts());
  };

  const handleOnApply = () => {
    dispatch(saveExpediteDailyParts({
      expediteDailyPartsList: expediteDailyParts,
      filters,
      pagination: expediteDailyPartsPagination,
    }));
  };

  const handleOnDelete = (deletedDailyPartList) => {
    deletedDailyPartList.forEach((dailyPart) => {
      dispatch(deleteExpediteDailyPart(dailyPart));
    });
  };

  const { applyDefaultSort, ...paginationOptions } = usePagination({
    filters,
    dispatch,
    listType: 'expediteDailyParts',
    pagination: expediteDailyPartsPagination,
    searchAction: getExpediteDailyParts,
  });

  const { page = 0, total = 0, loadingMore = false } = expediteDailyPartsPagination || { };

  return (
    <div className={classes.container}>
      <PF2MDataTable
        title={translate('common:DailyParts')}
        columns={applyDefaultSort(columns)}
        data={expediteDailyPartsList}
        page={page}
        totalCount={total}
        isLoading={loadingMore}
        options={{
          showTitle: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          search: true,
          selection: true,
          cacheKey: 'expediteDailyParts',
          editableRowValidation: rowData => (rowData.id < 0 && rowData.update_status !== 'I'),
          hideDelete: false,
          maxBodyHeight: 'calc(100vh - 350px)',
          minBodyHeight: 'calc(100vh - 320px)',
          showFirstLastPageButtons: false,
          ...paginationOptions,
        }}
        onAdd={handleOnAdd}
        onChange={handleOnChange}
        onDiscard={handleDiscard}
        onApply={handleOnApply}
        onDelete={handleOnDelete}
      />
    </div>
  );
};

ExpediteReport.propTypes = {
  classes: PropTypes.object.isRequired,
};

ExpediteReport.defaultProps = {};

export default withStyles(styles)(ExpediteReport);
